// The Phase 2 API returns exactly these shapes. Phase 1 fabricates them locally.

export type SourceSystem =
  | 'mowazaf'
  | 'sapSuccessFactors'
  | 'oracleHcm'
  | 'microsoft365'
  | 'zohoPeople'
  | 'bayzat';

/**
 * System types a company can actually register today.
 *
 * SAP SuccessFactors alone, because it is the only one with a connector behind it. Nothing else is
 * decided, and naming an undecided vendor on this screen is a promise the product cannot keep, so
 * the wizard says more are coming without saying which.
 *
 * `SourceSystem` still declares the others: the fixtures and the type are what a second vendor will
 * plug into, and Oracle HCM's fixture in particular is what proves the mapping model is necessary
 * (it names the same fields differently). Adding a vendor here is the single switch that surfaces it,
 * and it should only be flipped once a connector exists.
 */
export const CONNECTABLE_SYSTEMS: SourceSystem[] = ['sapSuccessFactors'];

/**
 * How data moves between the source system and Mowazaf.
 *
 * Three transports, not three tiers of assurance. Two of them are PULLS — Mowazaf asks the source
 * for data on a schedule — and one is a PUSH, where the source calls Mowazaf as changes happen.
 * Every transport therefore has an answer to "how current is this?", which is `syncFrequency` for a
 * pull and "as it happens" for a push.
 *
 * Signing the data exchange summary used to sit in this list as a fourth method. It is not a
 * transport: it is a governance gate that can apply to any of them, so it is now the independent
 * `requiresSignedExchange` flag. As a method it forced a customer who needed written sign-off to
 * also accept the API transport, left the question "which protocol does approved exchange use?"
 * unanswerable, and made a signed file feed unexpressible.
 */
export type ConnectionMethod = 'apiPull' | 'fileFeed' | 'webhookPush';

/** How often a pull runs. A push has no schedule — it arrives when the source sends it. */
export type SyncFrequency = 'hourly' | 'daily' | 'weekly';

/** The transports Mowazaf initiates, and therefore the ones that need a frequency. */
export function isPullMethod(method: ConnectionMethod): boolean {
  return method !== 'webhookPush';
}

export type SourceStatus = 'connected' | 'error' | 'notConnected';

/**
 * A registered EXTERNAL system, and nothing else.
 *
 * Mowazaf is deliberately absent from this collection. "Managed in Mowazaf" is an answer an
 * integration point holds (see `PointOwner`), not a member of the source list. A synthetic row here
 * would be a sentinel that becomes a real `CompanyIntegration` FK in Phase 2, with every credential,
 * method and health column null and special-cased in every join, every connector resolution and every
 * health fan-out. Section 5 of the spec already rules that pattern out for `notTracked`; it is ruled
 * out here for the same reason.
 */
export interface DataSource {
  id: string;
  system: SourceSystem;
  /** Customer-chosen label, so two tenants of one system are tellable apart. */
  displayName: string;
  status: SourceStatus;
  method?: ConnectionMethod;
  tenantUrl?: string;
  clientId?: string;
  /** Where the source leaves its export. `fileFeed` only — an API tenant URL cannot stand in for it. */
  fileLocation?: string;
  /** The endpoint Mowazaf issues for the source to call. `webhookPush` only. */
  callbackUrl?: string;
  grantedScopes: string[];
  connectedByName?: string;
  connectedOn?: string;
  lastRunOn?: string;
  nextScheduledRun?: string;
  /** How often the pull runs. Undefined for `webhookPush`, which has no schedule to state. */
  syncFrequency?: SyncFrequency;
  /** Only meaningful for a daily or weekly pull; an hourly one has no time of day. */
  syncTimeOfDay?: string;
  /**
   * Hold every import until the data exchange summary is signed. Independent of `method`, because
   * the question it answers ("has someone agreed in writing which fields move?") is unrelated to how
   * the bytes travel. Most customers need no gate at all.
   */
  requiresSignedExchange?: boolean;
  exchangeSignedByName?: string;
  exchangeSignedOn?: string;
}

export type ModuleKey = 'coreHr' | 'employeeImpact' | 'assets';

export type DataTypeKey =
  | 'employees'
  | 'departments'
  | 'jobRoles'
  | 'goals'
  | 'skills'
  | 'assets';

export type IntegrationPointStatus =
  | 'connected'
  | 'notSet'
  | 'native'
  | 'notTracked'
  | 'sourceError'
  | 'notOffered';

export type MatchKey = 'workEmail' | 'employeeNumber' | 'nationalId';

export interface ValueMapEntry {
  sourceValue: string;
  /** null means the customer has not decided yet, which becomes a preview warning. */
  targetValue: string | null;
}

export interface FieldMapping {
  /** null means unmapped. Required targets with a null source block the import. */
  sourceField: string | null;
  /** Shown next to date fields so a dd/MM source cannot be silently misread. */
  sourceFormatHint?: string;
  targetField: string;
  targetLabelKey: string;
  required: boolean;
  valueMap?: ValueMapEntry[];
}

/**
 * Who authors one data type. Exactly one answer, always present.
 *
 * This is the discriminator Phase 2 stores alongside the source FK. The two cannot be collapsed into
 * one nullable FK: "no decision yet" and "we maintain this in Mowazaf" would both be a null FK, and
 * they are different answers that drive different statuses and different summary counts. Making row
 * existence carry the difference instead is worse, because seeding all six rows would silently move
 * every tenant from `unset` to `mowazaf`.
 */
export type PointOwner = 'unset' | 'mowazaf' | 'external' | 'notTracked';

export interface IntegrationPoint {
  dataType: DataTypeKey;
  module: ModuleKey;
  owner: PointOwner;
  /**
   * Set if and only if `owner === 'external'`, and null for every other answer. Phase 2 stores this
   * as the nullable `SourceIntegrationId` FK with a CHECK constraint enforcing exactly that pairing,
   * so the illegal combinations are unrepresentable in the database rather than merely unwritten.
   */
  sourceId: string | null;
  lastUpdatedOn?: string;
  /** Mapping belongs to the pair (dataType, sourceId). Changing the source resets it. */
  mapping: FieldMapping[];
  matchKey?: MatchKey;
  /** True once enrichment has derived the Mowazaf-only fields for the imported rows. */
  enriched: boolean;
}

export type SyncTrigger = 'manual' | 'scheduled' | 'test' | 'config' | 'enrichment';

export interface SyncRun {
  id: string;
  occurredOn: string;
  dataType?: DataTypeKey;
  sourceId?: string;
  trigger: SyncTrigger;
  actorName: string;
  created: number;
  updated: number;
  skipped: number;
  durationMs: number;
  outcome: 'success' | 'failed';
  /** Translation key for a configuration change, so the trail localises. */
  messageKey?: string;
  /** Free text appended to the localised message, for example a source display name. */
  messageValue?: string;
}

export interface PreviewIssue {
  reasonKey: string;
  count: number;
  exampleLabel: string;
}

export interface PreviewResult {
  dataType: DataTypeKey;
  newCount: number;
  updatedCount: number;
  warnings: PreviewIssue[];
  skipped: PreviewIssue[];
  duplicates: PreviewIssue[];
  /** Required targets with no source field. The import button is disabled while this is non-empty. */
  blockingFields: string[];
}

/** Counters for the Configuration tab header. */
export interface SourceSummary {
  connected: number;
  errored: number;
  total: number;
}

/** Counters for the Integration points tab header. */
export interface PointSummary {
  mapped: number;
  notSet: number;
  notTracked: number;
  total: number;
}
